import {Injectable} from '@angular/core';
import {AnyData} from "@gp/shared/api";

interface HandsontableColumnMapper {
  toPhysicalColumn?: (visualCol: number) => number;
}

@Injectable()
export class ColHeaderTooltipService {

  attachTooltip(visualCol: number, TH: HTMLTableCellElement, tooltipHeaders: string[], table: HandsontableColumnMapper): void {
    if (!tooltipHeaders?.length) {
      return;
    }

    this.unbindTooltipFromTH(TH);

    const physicalCol = table?.toPhysicalColumn ? table.toPhysicalColumn(visualCol) : visualCol;

    if (physicalCol < 0 || physicalCol >= tooltipHeaders.length) {
      return;
    }

    const headerText = tooltipHeaders[physicalCol] || '';
    if (!headerText) {
      return;
    }

    TH.removeAttribute('title');
    (TH as AnyData).__gpTooltipBound = true;

    let tooltipEl: HTMLDivElement | null = null;

    const removeTooltip = () => {
      tooltipEl?.remove();
      tooltipEl = null;
      document.removeEventListener('scroll', removeTooltip, true);
      window.removeEventListener('popstate', removeTooltip);
    };

    const onMouseEnter = () => {
      tooltipEl = this.createTooltipElement(headerText);
      document.body.appendChild(tooltipEl);
      this.positionTooltip(tooltipEl, TH);
      document.addEventListener('scroll', removeTooltip, {capture: true, passive: true});
      window.addEventListener('popstate', removeTooltip);
    };

    (TH as AnyData).__gpMouseEnter = onMouseEnter;
    (TH as AnyData).__gpMouseLeave = removeTooltip;
    (TH as AnyData).__gpMouseDown = removeTooltip;

    TH.addEventListener('mouseenter', onMouseEnter);
    TH.addEventListener('mouseleave', removeTooltip);
    TH.addEventListener('mousedown', removeTooltip);
  }

  clearAllTooltips(view: HTMLElement): void {
    view?.querySelectorAll('th').forEach((th: Element) => {
      this.unbindTooltipFromTH(th as HTMLTableCellElement);
    });
  }

  unbindTooltipFromTH(TH: HTMLTableCellElement): void {
    const el = TH as AnyData;
    if (el.__gpMouseEnter) {
      TH.removeEventListener('mouseenter', el.__gpMouseEnter);
      delete el.__gpMouseEnter;
    }
    if (el.__gpMouseDown) {
      TH.removeEventListener('mousedown', el.__gpMouseDown);
      delete el.__gpMouseDown;
    }
    if (el.__gpMouseLeave) {
      el.__gpMouseLeave();
      TH.removeEventListener('mouseleave', el.__gpMouseLeave);
      delete el.__gpMouseLeave;
    }
    delete el.__gpTooltipBound;
  }

  private createTooltipElement(text: string): HTMLDivElement {
    const wrapper = document.createElement('div');
    wrapper.style.cssText = 'position:fixed; z-index:99999; pointer-events:none; overflow:visible;';

    const bubble = document.createElement('div');
    bubble.style.cssText = `
      position: relative;
      background: #333;
      color: #fff;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 12px;
      white-space: normal;
      max-width: 300px;
      word-break: break-word;
      box-shadow: 0 2px 6px rgba(0,0,0,0.3);
    `;
    bubble.textContent = text;

    const arrow = document.createElement('div');
    arrow.style.cssText = `
      position: absolute;
      bottom: -6px;
      width: 0;
      height: 0;
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
      border-top: 6px solid #333;
    `;
    bubble.appendChild(arrow);
    wrapper.appendChild(bubble);
    return wrapper;
  }

  private positionTooltip(tooltipEl: HTMLDivElement, TH: HTMLTableCellElement): void {
    const bubble = tooltipEl.firstElementChild as HTMLElement;
    const arrow = bubble.lastElementChild as HTMLElement;

    const thRect = TH.getBoundingClientRect();
    const bubbleRect = bubble.getBoundingClientRect();
    const thCenterX = thRect.left + thRect.width / 2;

    let top = thRect.top - bubbleRect.height - 6;
    let left = thCenterX - bubbleRect.width / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - bubbleRect.width - 8));

    if (top < 8) {
      top = thRect.bottom;
      arrow.style.bottom = 'auto';
      arrow.style.top = '-6px';
      arrow.style.borderTop = 'none';
      arrow.style.borderBottom = '6px solid #333';
    }

    arrow.style.left = `${Math.max(6, Math.min(thCenterX - left - 6, bubbleRect.width - 12))}px`;

    tooltipEl.style.top = `${top}px`;
    tooltipEl.style.left = `${left}px`;
  }
}
